import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { MessageSquare, Search, Filter, ChevronRight, Clock, AlertTriangle } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';

const stageLabels = {
  GREETING: 'Karşılama', DISCOVERY: 'Keşif', AVAILABILITY_CHECK: 'Müsaitlik',
  PRICE_INQUIRY: 'Fiyat', OBJECTION_HANDLING: 'İtiraz',
  RESERVATION_COLLECTION: 'Rezervasyon', CONFIRMATION: 'Onay', COMPLETED: 'Tamamlandı',
};
const stageColors = {
  GREETING: '#94a3b8', DISCOVERY: '#3b82f6', AVAILABILITY_CHECK: '#06b6d4',
  PRICE_INQUIRY: '#f59e0b', OBJECTION_HANDLING: '#f97316',
  RESERVATION_COLLECTION: '#8b5cf6', CONFIRMATION: '#22c55e', COMPLETED: '#10b981',
};

function Avatar({ name, size = 42 }) {
  const colors = ['#6366f1', '#3b82f6', '#ec4899', '#22c55e', '#f59e0b', '#8b5cf6'];
  const idx = (name || '?').charCodeAt(0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${colors[idx]}, ${colors[(idx + 1) % colors.length]})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: 'white',
    }}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

export default function ConversationsPage() {
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', channel: '', page: 1 });
  const [search, setSearch] = useState('');
  const [takeoverAlerts, setTakeoverAlerts] = useState([]);
  const navigate = useNavigate();
  const socket = useSocket();

  useEffect(() => {
    if (!socket) return;
    socket.on('takeover-required', ({ conversationId }) => {
      setTakeoverAlerts(prev => prev.includes(conversationId) ? prev : [...prev, conversationId]);
      toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={16} color="#f59e0b" />
          <span style={{ fontSize: 13 }}>Müşteri yardım istiyor — konuşmayı devralın</span>
          <button onClick={() => { navigate(`/conversations/${conversationId}`); toast.dismiss(t.id); }}
            style={{ marginLeft: 8, background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            Aç
          </button>
        </div>
      ), { duration: 10000, icon: null });
    });
    socket.on('new-message', () => {
      setFilters(f => ({ ...f }));
    });
    return () => { socket.off('takeover-required'); socket.off('new-message'); };
  }, [socket]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.channel) params.set('channel', filters.channel);
    params.set('page', filters.page);
    params.set('limit', 20);
    api.get(`/conversations?${params}`)
      .then(data => { setConversations(data.conversations); setTotal(data.total); })
      .finally(() => setLoading(false));
  }, [filters]);

  const filtered = conversations.filter(c =>
    !search || (c.customerName || '').toLowerCase().includes(search.toLowerCase())
  );

  const statusCounts = {
    all: conversations.length,
    active: conversations.filter(c => c.status === 'ACTIVE').length,
    taken: conversations.filter(c => c.isTakenOver).length,
  };

  return (
    <div style={{ padding: 28, height: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: '#1e293b' }}>
          Canlı İnbox
        </h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
          Tüm kanallardan gelen müşteri konuşmaları · {total} toplam
        </p>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'flex', gap: 12 }}>
        {[
          { label: 'Tüm Konuşmalar', count: statusCounts.all, color: '#6366f1', bg: '#ede9fe' },
          { label: 'Aktif', count: statusCounts.active, color: '#22c55e', bg: '#dcfce7' },
          { label: 'Devralınan', count: statusCounts.taken, color: '#f59e0b', bg: '#fef3c7' },
          ...(takeoverAlerts.length > 0 ? [{ label: '🔔 Yardım İstiyor', count: takeoverAlerts.length, color: '#ef4444', bg: '#fee2e2' }] : []),
        ].map(s => (
          <div key={s.label} style={{
            background: 'white', borderRadius: 12, padding: '12px 18px',
            border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{s.label}</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginLeft: 4 }}>{s.count}</span>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Müşteri ara..."
            style={{ paddingLeft: 34, fontSize: 13 }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { label: 'Tümü', value: '' },
            { label: 'Aktif', value: 'ACTIVE' },
            { label: 'Tamamlandı', value: 'COMPLETED' },
            { label: 'Terk', value: 'ABANDONED' },
          ].map(opt => (
            <button key={opt.value} onClick={() => setFilters({ ...filters, status: opt.value, page: 1 })}
              style={{
                padding: '7px 14px', borderRadius: 8, border: 'none', fontSize: 13, cursor: 'pointer', fontWeight: 600,
                background: filters.status === opt.value ? 'linear-gradient(135deg, #6366f1, #3b82f6)' : 'white',
                color: filters.status === opt.value ? 'white' : '#64748b',
                boxShadow: filters.status === opt.value ? '0 2px 8px rgba(99,102,241,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'all 0.2s',
              }}
            >{opt.label}</button>
          ))}
        </div>
        <select value={filters.channel} onChange={e => setFilters({ ...filters, channel: e.target.value, page: 1 })}
          style={{ width: 140, fontSize: 13 }}>
          <option value="">Tüm Kanallar</option>
          <option value="INSTAGRAM">📸 Instagram</option>
          <option value="WHATSAPP">💬 WhatsApp</option>
        </select>
      </div>

      {/* Conversation List */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#94a3b8', gap: 10 }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #6366f1', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
          Yükleniyor...
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden', flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <MessageSquare size={44} color="#e2e8f0" style={{ margin: '0 auto 12px' }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>Konuşma bulunamadı</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Filtrelerinizi değiştirmeyi deneyin</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Table Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1fr 2fr 1fr 40px', gap: 12, padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                {['MÜŞTERİ', 'KANAL', 'AŞAMA', 'DURUM', 'SON MESAJ', 'TARİH', ''].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>{h}</div>
                ))}
              </div>
              {filtered.map((c, i) => (
                <div key={c.id} onClick={() => navigate(`/conversations/${c.id}`)}
                  style={{
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1fr 2fr 1fr 40px', gap: 12,
                    padding: '14px 20px', alignItems: 'center', cursor: 'pointer',
                    borderBottom: i < filtered.length - 1 ? '1px solid #f8fafc' : 'none',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fafbff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                >
                  {/* Customer */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <Avatar name={c.customerName} size={36} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{c.customerName || 'Bilinmeyen'}</div>
                      {c.customerPhone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.customerPhone}</div>}
                    </div>
                  </div>
                  {/* Channel */}
                  <div>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999,
                      background: c.channel === 'INSTAGRAM' ? '#dbeafe' : '#dcfce7',
                      color: c.channel === 'INSTAGRAM' ? '#1d4ed8' : '#15803d',
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {c.channel === 'INSTAGRAM' ? '📸' : '💬'} {c.channel}
                    </span>
                  </div>
                  {/* Stage */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: stageColors[c.stage] || '#94a3b8', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{stageLabels[c.stage] || c.stage}</span>
                  </div>
                  {/* Status */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span className={`badge ${c.status === 'ACTIVE' ? 'badge-green' : c.status === 'COMPLETED' ? 'badge-blue' : 'badge-gray'}`}>
                      {c.status === 'ACTIVE' ? 'Aktif' : c.status === 'COMPLETED' ? 'Bitti' : 'Terk'}
                    </span>
                    {c.isTakenOver && <span className="badge badge-yellow">Devralındı</span>}
                  </div>
                  {/* Last message */}
                  <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.messages?.[0]?.content || '—'}
                  </div>
                  {/* Date */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    <Clock size={11} />
                    {format(new Date(c.lastMessageAt), 'd MMM HH:mm', { locale: tr })}
                  </div>
                  <ChevronRight size={15} color="#cbd5e1" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
